import React, { useState } from 'react';
import { 
  Pill, 
  Syringe, 
  Flame,
  Clock, 
  Calendar, 
  Building2, 
  Target, 
  Edit3, 
  Copy,
  Trash2, 
  History, 
  MoreVertical,
  CheckCircle2,
  PauseCircle,
  XCircle,
  FileText,
  Image as ImageIcon
} from 'lucide-react';

import {
  getEffectivePrimaryCategory,
  getEffectiveSecondaryCategory,
  getCategoryConfig
} from '../utils/healthCategories';
import { formatDosageRange } from '../utils/sortUtils';

export default function ProtocolCard({ 
  protocol, 
  onEdit, 
  onDuplicate,
  onStatusChange, 
  onDelete, 
  onViewHistory,
  onExportImage
}) {
  const [showMenu, setShowMenu] = useState(false);

  const {
    id,
    name,
    category,
    weeklyTotalDosage,
    unit,
    administrationFormat,
    timingOfDay,
    frequencySchedule,
    brandOrSupplier,
    intendedOutcome,
    notes,
    cycleStatus,
    startDate,
    endDate
  } = protocol;

  // Format Dosage Display (shared helper: `min unit – max unit`)
  const formatDosage = () => formatDosageRange(protocol);

  // Category Badge Helper
  const getCategoryBadge = () => {
    switch (category) {
      case 'Peptide':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase bg-purple-500/15 text-purple-300 border border-purple-500/30">
            <Syringe className="w-3 h-3 mr-1" />
            {category}
          </span>
        );
      case 'Hormones & AAS':
      case 'Hormone':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase bg-rose-500/15 text-rose-300 border border-rose-500/30">
            <Flame className="w-3 h-3 mr-1" />
            Hormones & AAS
          </span>
        );
      case 'Supplement':
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase bg-sky-500/15 text-sky-300 border border-sky-500/30">
            <Pill className="w-3 h-3 mr-1" />
            {category}
          </span>
        );
    }
  };

  // Status Styling
  const getStatusBadge = () => {
    switch (cycleStatus) {
      case 'Active':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
            <span>Active</span>
          </span>
        );
      case 'Pending':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
            <Clock className="w-3 h-3" />
            <span>Pending</span>
          </span>
        );
      case 'Paused':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <PauseCircle className="w-3 h-3" />
            <span>Paused</span>
          </span>
        );
      case 'Completed':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-500/20 text-slate-400 border border-slate-500/30">
            <CheckCircle2 className="w-3 h-3" />
            <span>Completed</span>
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="glass-card rounded-2xl p-5 relative flex flex-col justify-between group">
      
      {/* Top Header Row */}
      <div>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center space-x-2 mb-1">
              {getCategoryBadge()}
              {getStatusBadge()}
            </div>

            <h3 className="text-base font-bold text-white group-hover:text-sky-300 transition">
              {name}
            </h3>

            {/* Health Target Badges */}
            {(() => {
              const primaryCat = getEffectivePrimaryCategory(protocol);
              const secondaryCat = getEffectiveSecondaryCategory(protocol);
              const primaryConfig = getCategoryConfig(primaryCat);
              return (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide ${primaryConfig.badgeClass}`}>
                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${primaryConfig.dotClass}`} />
                    {primaryCat}
                  </span>
                  {secondaryCat && (
                    <span 
                      className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium text-slate-300 bg-slate-800/80 border border-slate-700/70"
                      title={`Secondary Target: ${secondaryCat}`}
                    >
                      {secondaryCat}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Action Menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
              title="Options"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showMenu && (
              <div 
                className="absolute right-0 top-8 z-20 w-44 bg-slate-900 border border-slate-800 rounded-xl shadow-xl py-1 text-xs text-slate-300 animate-fadeIn"
                onMouseLeave={() => setShowMenu(false)}
              >
                <button
                  onClick={() => { setShowMenu(false); onEdit(protocol); }}
                  className="w-full px-3 py-2 text-left hover:bg-slate-800 flex items-center space-x-2 transition text-slate-200"
                >
                  <Edit3 className="w-3.5 h-3.5 text-sky-400" />
                  <span>Edit Protocol</span>
                </button>

                <button
                  onClick={() => { setShowMenu(false); onDuplicate && onDuplicate(protocol); }}
                  className="w-full px-3 py-2 text-left hover:bg-slate-800 flex items-center space-x-2 transition text-slate-200"
                >
                  <Copy className="w-3.5 h-3.5 text-indigo-400" />
                  <span>Duplicate Protocol</span>
                </button>

                <button
                  onClick={() => { setShowMenu(false); onViewHistory(protocol); }}
                  className="w-full px-3 py-2 text-left hover:bg-slate-800 flex items-center space-x-2 transition text-slate-200"
                >
                  <History className="w-3.5 h-3.5 text-purple-400" />
                  <span>View History Log</span>
                </button>

                <button
                  onClick={() => { setShowMenu(false); onExportImage && onExportImage(protocol); }}
                  className="w-full px-3 py-2 text-left hover:bg-slate-800 flex items-center space-x-2 transition text-slate-200"
                >
                  <ImageIcon className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Export Image</span>
                </button>

                <div className="my-1 border-t border-slate-800"></div>

                <div className="px-3 py-1 text-[10px] font-semibold text-slate-500 uppercase">
                  Quick Status Change
                </div>
                {['Active', 'Pending', 'Paused', 'Completed'].map((st) => (
                  <button
                    key={st}
                    onClick={() => { setShowMenu(false); onStatusChange(protocol, st); }}
                    className={`w-full px-3 py-1.5 text-left hover:bg-slate-800 flex items-center justify-between transition ${
                      cycleStatus === st ? 'text-sky-400 font-semibold' : 'text-slate-400'
                    }`}
                  >
                    <span>Set to {st}</span>
                    {cycleStatus === st && <span className="w-1.5 h-1.5 rounded-full bg-sky-400"></span>}
                  </button>
                ))}

                <div className="my-1 border-t border-slate-800"></div>

                <button
                  onClick={() => { setShowMenu(false); onDelete(protocol); }}
                  className="w-full px-3 py-2 text-left hover:bg-red-500/10 text-red-400 flex items-center space-x-2 transition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Dosage Callout */}
        <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 mb-4 flex items-center justify-between">
          <div className="text-xs text-slate-400 font-medium">Dosage Protocol</div>
          <div className="text-right">
            <div className="text-sm font-extrabold text-sky-400 tracking-wide font-mono">
              {formatDosage()}
            </div>
            {weeklyTotalDosage !== undefined && weeklyTotalDosage !== null && weeklyTotalDosage !== '' && (
              <div className="text-[11px] font-medium text-slate-400 font-mono mt-0.5">
                Weekly: {weeklyTotalDosage} {unit}
              </div>
            )}
          </div>
        </div>

        {/* Metadata Details Grid */}
        <div className="space-y-2 text-xs text-slate-300">
          
          <div className="flex items-center space-x-2 text-slate-300">
            <Clock className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            <span className="text-slate-400">Timing:</span>
            <span className="font-medium text-slate-200">{timingOfDay}</span>
          </div>

          <div className="flex items-center space-x-2 text-slate-300">
            <Calendar className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            <span className="text-slate-400">Schedule:</span>
            <span className="font-medium text-slate-200">{frequencySchedule}</span>
          </div>

          <div className="flex items-center space-x-2 text-slate-300">
            <Syringe className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            <span className="text-slate-400">Format:</span>
            <span className="font-medium text-slate-200">{administrationFormat}</span>
          </div>

          {brandOrSupplier && (
            <div className="flex items-center space-x-2 text-slate-300">
              <Building2 className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
              <span className="text-slate-400">Brand/Supplier:</span>
              <span className="font-medium text-slate-200">{brandOrSupplier}</span>
            </div>
          )}

          {intendedOutcome && (
            <div className="flex items-start space-x-2 text-slate-300 pt-1">
              <Target className="w-3.5 h-3.5 text-sky-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-slate-400 mr-1">Outcome:</span>
                <span className="text-slate-300">{intendedOutcome}</span>
              </div>
            </div>
          )}

          {notes && (
            <div className="flex items-start space-x-2 text-slate-300 pt-1 text-[11px] text-slate-400 italic bg-slate-950/40 p-2 rounded-lg border border-slate-800/60">
              <FileText className="w-3.5 h-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
              <span>{notes}</span>
            </div>
          )}

        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-4 pt-3 border-t border-slate-800/60 flex items-center justify-between text-[11px] text-slate-500">
        <div>Started: {startDate || 'N/A'}</div>
        {endDate && <div>Ended: {endDate}</div>}
      </div>

    </div>
  );
}
